import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Users, Building2, CreditCard, Ticket, TrendingUp, DollarSign } from 'lucide-react';
import { getUsers, getCompanies, getSubscriptions, getInvoices, getTickets } from '../../services/storage';

export const Dashboard = () => {
  const { user } = useAuth();
  
  const stats = {
    totalUsers: getUsers().length,
    totalCompanies: getCompanies().length,
    activeSubscriptions: getSubscriptions().filter(s => s.status === 'active').length,
    pendingInvoices: getInvoices().filter(i => i.status === 'pending').length,
    openTickets: getTickets().filter(t => t.status === 'open').length,
    monthlyRevenue: getInvoices()
      .filter(i => i.status === 'paid')
      .reduce((sum, inv) => sum + inv.amount, 0)
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Dashboard Admin | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
            <p className="text-neutral-600">Visão geral do sistema</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-500">Bem-vindo,</span>
            <span className="text-sm font-medium text-neutral-900">{user?.name}</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Total de Usuários</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.totalUsers}</p>
              </div>
              <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-blue" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Empresas</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.totalCompanies}</p>
              </div>
              <div className="w-12 h-12 bg-primary-orange/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-orange" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Assinaturas Ativas</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.activeSubscriptions}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Faturas Pendentes</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.pendingInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Tickets Abertos</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">{stats.openTickets}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Ticket className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Receita Total</p>
                <p className="text-3xl font-bold text-neutral-900 mt-1">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(stats.monthlyRevenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-neutral-900 mb-4">Atividade Recente</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-primary-blue/10 rounded-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary-blue" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Nova empresa cadastrada</p>
                  <p className="text-sm text-neutral-600">Empresa Exemplo Ltda</p>
                </div>
              </div>
              <Badge variant="primary">Hoje</Badge>
            </div>

            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Pagamento confirmado</p>
                  <p className="text-sm text-neutral-600">Fatura #INV-2024-0001</p>
                </div>
              </div>
              <Badge variant="success">Hoje</Badge>
            </div>

            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Novo ticket de suporte</p>
                  <p className="text-sm text-neutral-600">Dúvida sobre configuração</p>
                </div>
              </div>
              <Badge variant="secondary">Ontem</Badge>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};
